import React, { useState, useMemo, useEffect } from 'react';
import { InboxItem, Priority } from '../types';
import {
  CheckCircle2,
  AlertTriangle,
  MessageSquare,
  BookOpen,
  Info,
  Filter,
  Search,
  Users,
  GanttChart,
  ExternalLink
} from 'lucide-react';
import './TaskContextView.css';

interface TaskContextViewProps {
  items: InboxItem[];
  onAccept: (taskId: string) => void;
  onClarify: (taskId: string) => void;
}

interface RelatedDocument {
  document: {
    doc_id: string;
    title: string;
    url: string;
    content: string;
  };
  relevance_score: number;
  matched_sections: Array<{
    section_id: string;
    heading: string;
    content: string;
  }>;
}

interface RelatedIssue {
  issue: {
    issue_id: string;
    title: string;
    description: string;
    severity: string;
    status: string;
    url: string;
  };
  relevance_score: number;
  why_relevant: string;
}

interface TaskDetailData {
  task: any;
  message_thread: any[];
  priority_breakdown: any;
  related_documents: RelatedDocument[];
  related_issues: RelatedIssue[];
  evidence_highlights: any[];
}

// Helper to calculate band from existing data
const calculateBand = (item: InboxItem): Priority => {
  // Use priority_band if available, otherwise calculate from impact_score
  if (item.priority_band) {
    return item.priority_band;
  }

  const score = item.impact_score;
  if (score >= 0.7) return Priority.RED;
  if (score >= 0.4) return Priority.YELLOW;
  return Priority.BLUE;
};

// Calculate tri-meter scores (use API values or estimate)
const calculateScores = (item: InboxItem) => {
  // Priority score is the main impact_score
  const priorityScore = item.impact_score;

  // Use urgency from API if available, otherwise estimate from deadline
  let urgency = item.urgency_score ?? 0.5;
  if (urgency === null && item.deadline_iso) {
    const deadline = new Date(item.deadline_iso);
    const now = new Date();
    const hoursUntil = (deadline.getTime() - now.getTime()) / (1000 * 60 * 60);

    if (hoursUntil < 24) urgency = 0.9;
    else if (hoursUntil < 72) urgency = 0.7;
    else urgency = 0.5;
  }

  // Use confidence from API if available, otherwise default to 0.7
  const confidence = item.confidence_score ?? 0.7;

  // Estimate impact from confidence and priority factors
  const impact = confidence * priorityScore;

  return {
    priorityScore,
    urgency,
    impact,
    confidence
  };
};

// Format deadline
const formatDeadline = (deadlineISO: string | null) => {
  if (!deadlineISO) return '—';
  const date = new Date(deadlineISO);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Overdue';
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays <= 7) return `${diffDays} days`;
  return date.toLocaleDateString();
};

const pct = (v: number) => Math.round(v * 100);

// Score rail component
function ScoreRail({ label, value }: { label: string; value: number }) {
  return (
    <div className="score-rail">
      <span className="score-label">{label}</span>
      <div className="score-track">
        <div className="score-fill" style={{ width: `${Math.min(100, Math.max(0, value * 100))}%` }} />
      </div>
      <span className="score-value">{pct(value)}%</span>
    </div>
  );
}

// Inbox card component
function InboxCard({ item, onSelect, isSelected }: {
  item: InboxItem;
  onSelect: () => void;
  isSelected: boolean;
}) {
  const band = calculateBand(item);
  const scores = calculateScores(item);

  return (
    <div
      className={`inbox-card ${band.toLowerCase()} ${isSelected ? 'selected' : ''}`}
      onClick={onSelect}
    >
      <div className="inbox-card-header">
        <span className={`band-badge ${band.toLowerCase()}`}>{band}</span>
        <span className="inbox-card-title">{item.title}</span>
      </div>

      <div className="inbox-card-meta">
        <span className="meta-item">P:{pct(scores.priorityScore)}%</span>
        <span className="meta-item">U:{pct(scores.urgency)}%</span>
        <span className="meta-item">I:{pct(scores.impact)}%</span>
        <span className="meta-item">C:{pct(scores.confidence)}%</span>
        {item.display_owner && (
          <span className="meta-item owner">
            <Users size={12} /> {item.display_owner}
          </span>
        )}
        {item.flags.includes('MISSING_DEADLINE') && (
          <span className="meta-item warning">
            <AlertTriangle size={12} /> Missing deadline
          </span>
        )}
      </div>

      <div className="inbox-card-chips">
        {item.why_chips.slice(0, 3).map((chip, idx) => (
          <span key={idx} className="chip">{chip}</span>
        ))}
      </div>
    </div>
  );
}

// Sidebar inbox component
function PriorityInbox({ items, selected, onSelect }: {
  items: InboxItem[];
  selected: InboxItem | null;
  onSelect: (item: InboxItem) => void;
}) {
  const [query, setQuery] = useState('');
  const [bandFilter, setBandFilter] = useState<Priority | 'ALL'>('ALL');
  const [userFilter, setUserFilter] = useState<string>('ALL');

  // Extract unique users from items (exclude channel names starting with #)
  const uniqueUsers = useMemo(() => {
    const users = new Set<string>();
    items.forEach(item => {
      const owner = item.owner_email || item.display_owner;
      if (owner && !owner.startsWith('#')) {
        users.add(owner);
      }
    });
    return Array.from(users).sort();
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const band = calculateBand(item);
      if (bandFilter !== 'ALL' && band !== bandFilter) return false;
      const owner = item.owner_email || item.display_owner;
      if (userFilter !== 'ALL' && owner !== userFilter) return false;
      if (query) {
        const searchText = `${item.title} ${owner || ''} ${item.why_chips.join(' ')}`.toLowerCase();
        if (!searchText.includes(query.toLowerCase())) return false;
      }
      return true;
    });
  }, [items, query, bandFilter, userFilter]);

  const grouped = useMemo(() => {
    const groups: Record<Priority, InboxItem[]> = {
      [Priority.RED]: [],
      [Priority.YELLOW]: [],
      [Priority.BLUE]: []
    };
    filtered.forEach((item) => {
      const band = calculateBand(item);
      groups[band].push(item);
    });
    return groups;
  }, [filtered]);

  const counts = {
    RED: items.filter(i => calculateBand(i) === Priority.RED).length,
    YELLOW: items.filter(i => calculateBand(i) === Priority.YELLOW).length,
    BLUE: items.filter(i => calculateBand(i) === Priority.BLUE).length
  };

  return (
    <div className="inbox-sidebar">
      <div className="inbox-sidebar-header">
        <div>
          <h1 className="inbox-title">ClarityHub</h1>
          <p className="inbox-subtitle">Measure what matters. Protect deep work.</p>
        </div>
        <div className="band-counts">
          <span className="band-count red">{counts.RED} RED</span>
          <span className="band-count yellow">{counts.YELLOW} YLW</span>
          <span className="band-count blue">{counts.BLUE} BLU</span>
        </div>
      </div>

      <div className="inbox-filters">
        <div className="filter-buttons">
          <Filter size={16} className="filter-icon" />
          {(['ALL', Priority.RED, Priority.YELLOW, Priority.BLUE] as const).map((b) => (
            <button
              key={b}
              className={`filter-btn ${bandFilter === b ? 'active' : ''}`}
              onClick={() => setBandFilter(b)}
            >
              {b}
            </button>
          ))}
        </div>
        <div className="filter-row">
          <div className="user-filter">
            <Users size={16} className="filter-icon" />
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="user-select"
            >
              <option value="ALL">All Users</option>
              {uniqueUsers.map((user) => (
                <option key={user} value={user}>
                  {user.split('@')[0]}
                </option>
              ))}
            </select>
          </div>
          <div className="search-box">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tasks, owners, factors…"
              className="search-input"
            />
          </div>
        </div>
      </div>

      <div className="inbox-section-label">
        <GanttChart size={16} /> Priority Inbox
      </div>

      <div className="inbox-list">
        {([Priority.RED, Priority.YELLOW, Priority.BLUE] as const).map((band) => (
          <div key={band} className="band-group">
            <div className={`band-group-header ${band.toLowerCase()}`}>
              <div className={`band-dot ${band.toLowerCase()}`} />
              {band} — {band === Priority.RED ? 'urgent (<24h / exec)' : band === Priority.YELLOW ? 'important (1–3d)' : 'regular'}
            </div>
            {grouped[band].map((item) => (
              <InboxCard
                key={item.task_id}
                item={item}
                onSelect={() => onSelect(item)}
                isSelected={selected?.task_id === item.task_id}
              />
            ))}
            {grouped[band].length === 0 && (
              <div className="empty-band">No {band.toLowerCase()} tasks.</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Task context view (right panel)
function TaskDetail({ item, onAccept, onClarify }: {
  item: InboxItem;
  onAccept: (id: string) => void;
  onClarify: (id: string) => void;
}) {
  const [clarifyOpen, setClarifyOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{role: string, content: string}>>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [taskDetail, setTaskDetail] = useState<TaskDetailData | null>(null);
  const [loading, setLoading] = useState(false);

  const band = calculateBand(item);
  const scores = calculateScores(item);
  const due = formatDeadline(item.deadline_iso);

  // Fetch task detail when item changes
  useEffect(() => {
    const fetchTaskDetail = async () => {
      setLoading(true);
      try {
        const response = await fetch(`http://localhost:8080/api/tasks/${item.task_id}/detail`);
        if (response.ok) {
          const data = await response.json();
          setTaskDetail(data);
        }
      } catch (error) {
        console.error('Failed to fetch task detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTaskDetail();
  }, [item.task_id]);

  // Extract issue IDs from task metadata
  const issueIds = taskDetail?.task?.metadata?.referenced_issues || [];

  const handleAccept = async () => {
    try {
      const response = await fetch(`http://localhost:8080/api/tasks/${item.task_id}/accept`, {
        method: 'POST'
      });

      if (response.ok) {
        onAccept(item.task_id);
        alert('Task accepted successfully!');
      } else {
        alert('Failed to accept task');
      }
    } catch (error) {
      console.error('Accept error:', error);
      alert('Error accepting task');
    }
  };

  const handleChatSend = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await fetch(`http://localhost:8080/api/tasks/${item.task_id}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage })
      });

      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response + (data.suggested_slack_question ? `\n\n**Suggested Question for Slack:**\n"${data.suggested_slack_question}"` : '')
        }]);
      } else {
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.'
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Error connecting to AI assistant.'
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="task-detail">
      {/* Header */}
      <div className="task-header">
        <div className="task-header-content">
          <div className="task-header-top">
            <span className={`band-badge ${band.toLowerCase()}`}>{band}</span>
            <h2 className="task-title">{item.title}</h2>
          </div>
          <div className="task-header-meta">
            <span className="meta-item">
              <Users size={16} /> Owner: {item.display_owner || item.owner_email || '—'}
            </span>
            <span className="meta-item">Due: {due}</span>
            {item.source_link && (
              <span className="meta-item">
                <MessageSquare size={16} />
                <a href={item.source_link} target="_blank" rel="noreferrer" className="link">
                  Slack
                </a>
              </span>
            )}
          </div>
        </div>
        <div className="task-actions-header">
          <button
            className="btn btn-secondary"
            onClick={() => {
              setClarifyOpen(!clarifyOpen);
              if (chatMessages.length === 0) {
                setChatMessages([{
                  role: 'assistant',
                  content: `Hi! I'm here to help you understand this task. I have access to the Slack thread context, relevant documentation, and related issues. What would you like to know?`
                }]);
              }
            }}
          >
            <MessageSquare size={16} /> AI Assistant
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAccept}
          >
            <CheckCircle2 size={16} /> Accept
          </button>
        </div>
      </div>

      {/* AI Chatbot Dialog */}
      {clarifyOpen && (
        <div className="clarify-dialog" style={{ maxHeight: '500px', height: '500px' }}>
          <div className="clarify-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0 }}>AI Task Assistant</h3>
              <button onClick={() => setClarifyOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>×</button>
            </div>
            <p className="clarify-hint" style={{ marginBottom: '12px' }}>Ask questions about this task. I can search Slack threads, documentation, and related issues.</p>

            {/* Chat messages */}
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '12px', padding: '12px', background: '#f5f5f5', borderRadius: '6px' }}>
              {chatMessages.map((msg, idx) => (
                <div key={idx} style={{ marginBottom: '12px', textAlign: msg.role === 'user' ? 'right' : 'left' }}>
                  <div style={{
                    display: 'inline-block',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    background: msg.role === 'user' ? '#007bff' : '#fff',
                    color: msg.role === 'user' ? '#fff' : '#333',
                    maxWidth: '80%',
                    textAlign: 'left',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div style={{ textAlign: 'left', color: '#666' }}>
                  <div style={{ display: 'inline-block', padding: '8px 12px' }}>Thinking...</div>
                </div>
              )}
            </div>

            {/* Chat input */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleChatSend()}
                placeholder="Ask about scope, timeline, requirements..."
                className="clarify-input"
                style={{ flex: 1 }}
                disabled={chatLoading}
              />
              <button
                className="btn btn-primary"
                onClick={handleChatSend}
                disabled={chatLoading || !chatInput.trim()}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Overview */}
      <div className="task-overview">
        <div className="card scores-card">
          <div className="scores-header">
            <span className="card-title">Priority Score (P)</span>
            <span className="score-display">{pct(scores.priorityScore)}%</span>
          </div>
          <div className="scores-rails">
            <div>
              <ScoreRail label="U" value={scores.urgency} />
            </div>
            <div>
              <ScoreRail label="I" value={scores.impact} />
            </div>
            <div>
              <ScoreRail label="C" value={scores.confidence} />
            </div>
          </div>
          <div style={{
            marginTop: '12px',
            padding: '10px 12px',
            background: '#f0f9ff',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#334155',
            lineHeight: '1.5'
          }}>
            <div style={{ marginBottom: '6px', fontWeight: '500' }}>
              Priority Score = ∛(U × I × C)
            </div>
            <div style={{ fontSize: '11px', color: '#64748b' }}>
              <strong>U (Urgency)</strong>: Time sensitivity - deadlines, ASAP keywords, blocking states<br/>
              <strong>I (Impact)</strong>: Business effect - customer facing, revenue impact, team blocking<br/>
              <strong>C (Confidence)</strong>: AI extraction clarity - clear ownership, explicit actions
            </div>
          </div>
          <div className="priority-formula" style={{
            marginTop: '8px',
            padding: '8px 12px',
            background: '#f8f9fa',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#6b7280',
            fontFamily: 'monospace'
          }}>
            P = ∛({pct(scores.urgency)}% × {pct(scores.impact)}% × {pct(scores.confidence)}%) = <strong>{pct(scores.priorityScore)}%</strong>
          </div>
          <div className="scores-chips">
            {item.why_chips.slice(0, 4).map((chip, idx) => (
              <span key={idx} className="chip" title={`Evidence factor: ${chip}`}>
                {chip}
              </span>
            ))}
          </div>
        </div>

        <div className="card summary-card">
          <h3 className="card-title">Summary</h3>
          <p className="summary-label">Why it matters:</p>
          <p className="summary-text">
            {/* Use why_it_matters from InboxItem (generated by ContextAwarePriorityEngine) */}
            {item.why_it_matters || 'This task requires your attention based on our analysis.'}
          </p>
          {item.flags.length > 0 && (
            <div className="flag-chips">
              {item.flags.map((flag, idx) => (
                <span key={idx} className="flag-chip" title={`System flag: ${flag.replace('_', ' ')}`}>
                  {flag.replace('_', ' ')}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Context sections */}
      <div className="context-grid">
        {/* Documentation card */}
        <div className="card docs-card">
          <div className="card-header">
            <BookOpen size={16} /> Relevant Documentation
          </div>
          {loading ? (
            <div className="loading-section">Loading...</div>
          ) : taskDetail?.related_documents && taskDetail.related_documents.length > 0 ? (
            <div className="docs-list">
              {taskDetail.related_documents.map((doc) => (
                <div key={doc.document.doc_id} className="doc-item">
                  <div className="doc-header">
                    <BookOpen size={14} className="doc-icon" />
                    <a
                      href={doc.document.url}
                      target="_blank"
                      rel="noreferrer"
                      className="doc-title"
                    >
                      {doc.document.title}
                    </a>
                    <span className="doc-relevance">
                      {Math.round(doc.relevance_score * 100)}% match
                    </span>
                  </div>
                  {doc.matched_sections && doc.matched_sections.length > 0 && (
                    <div className="doc-sections">
                      {doc.matched_sections.slice(0, 2).map((section, idx) => (
                        <div key={idx} className="doc-section">
                          <span className="section-heading">{section.heading}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-section">No relevant docs found</div>
          )}
        </div>

        {/* Issues panel - now takes full width */}
        <div className="card issues-card">
          <div className="card-header">
            <AlertTriangle size={16} /> Associated Issues
          </div>
          {loading ? (
            <div className="loading-section">Loading...</div>
          ) : (issueIds.length > 0 || (taskDetail?.related_issues && taskDetail.related_issues.length > 0)) ? (
            <div className="issues-list">
              {/* Extracted issue IDs from metadata */}
              {issueIds.length > 0 && (
                <div className="issues-section">
                  <div className="issues-section-label">Mentioned in Thread</div>
                  {issueIds.map((issueId: string) => (
                    <div key={issueId} className="issue-item">
                      <AlertTriangle size={14} className="issue-icon" />
                      <span className="issue-id">{issueId}</span>
                      <a
                        href={`https://jira.company.com/browse/${issueId}`}
                        target="_blank"
                        rel="noreferrer"
                        className="issue-link"
                      >
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {/* Semantically matched issues from Elasticsearch */}
              {taskDetail?.related_issues && taskDetail.related_issues.length > 0 && (
                <div className="issues-section">
                  <div className="issues-section-label">Semantically Related</div>
                  {taskDetail.related_issues.map((relatedIssue) => {
                    const getSeverityColor = (severity: string) => {
                      const sev = severity.toLowerCase();
                      if (sev === 'critical') return 'severity-critical';
                      if (sev === 'high') return 'severity-high';
                      if (sev === 'medium') return 'severity-medium';
                      if (sev === 'low') return 'severity-low';
                      return 'severity-default';
                    };

                    return (
                      <div key={relatedIssue.issue.issue_id} className="semantic-issue-item">
                        <div className="semantic-issue-header">
                          <div className="semantic-issue-title-row">
                            <a
                              href={relatedIssue.issue.url}
                              target="_blank"
                              rel="noreferrer"
                              className="semantic-issue-title"
                            >
                              <span className="semantic-issue-id">{relatedIssue.issue.issue_id}</span>
                              <span className="semantic-issue-text">{relatedIssue.issue.title}</span>
                              <ExternalLink size={12} className="semantic-issue-link-icon" />
                            </a>
                          </div>
                          <div className="semantic-issue-badges">
                            <span className={`severity-badge ${getSeverityColor(relatedIssue.issue.severity)}`}>
                              {relatedIssue.issue.severity}
                            </span>
                            <span className="relevance-badge">
                              {Math.round(relatedIssue.relevance_score * 100)}% match
                            </span>
                          </div>
                        </div>
                        {relatedIssue.why_relevant && (
                          <div className="semantic-issue-reason">
                            <Info size={12} className="reason-icon" />
                            <span className="reason-text">{relatedIssue.why_relevant}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="empty-section">No issues detected</div>
          )}
        </div>
      </div>
    </div>
  );
}

// Main component
const TaskContextView: React.FC<TaskContextViewProps> = ({ items, onAccept, onClarify }) => {
  const [selected, setSelected] = useState<InboxItem | null>(items.length > 0 ? items[0] : null);

  if (items.length === 0) {
    return (
      <div className="inbox-empty">
        <div className="empty-icon">📭</div>
        <h2>Inbox Zero!</h2>
        <p>No actionable items at the moment. Time for deep work.</p>
      </div>
    );
  }

  return (
    <div className="task-context-view">
      <PriorityInbox items={items} selected={selected} onSelect={setSelected} />
      <div className="task-panel">
        {selected ? (
          <TaskDetail item={selected} onAccept={onAccept} onClarify={onClarify} />
        ) : (
          <div className="empty-selection">
            <p>Select a task to view context</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskContextView;
